import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Badge,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  FileDownload as ExportIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://printformee.com/api";

const statusColors = {
  pending: "warning",
  confirmed: "info",
  processing: "secondary",
  "out-for-delivery": "warning",
  delivered: "success",
  returned: "error",
  cancelled: "error",
  failed: "error",
  canceled: "error",
  packaging: "secondary",
};

const paymentStatusColors = {
  paid: "success",
  unpaid: "error",
};

function Orders({ status: statusProp, showCancelled = false, title }) {
  const navigate = useNavigate();
  const { status: statusParam } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Use status from prop first, then from URL params
  const currentStatus = statusProp || statusParam || "all";

  // Determine page title
  const pageTitle =
    title ||
    (currentStatus === "all"
      ? "All Orders"
      : `${currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)} Orders`);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    orderType: "all",
    store: "all",
    customer: "all",
    dateType: "order",
    orderStatus: currentStatus,
    paymentStatus: "all",
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Build query parameters
      const params = {
        page,
        limit: 10,
        ...(search && { search }),
        ...filters,
      };

      // If showCancelled is true and currentStatus is 'failed', include both failed and cancelled orders
      if (showCancelled && currentStatus === "failed") {
        params.status = ["failed", "cancelled"];
      }

      const response = await axios.get(`${API_BASE_URL}/orders`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("🔍 ADMIN FRONTEND - API Response:", response.data);
      console.log(
        "🔍 ADMIN FRONTEND - Orders count:",
        response.data.orders?.length,
      );

      setOrders(response.data.orders || []);
      setTotalPages(response.data.pagination?.pages || 0);
      setTotalOrders(response.data.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      setTotalPages(0);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, search, filters]);

  // Update orderStatus filter when status changes
  useEffect(() => {
    setFilters((prev) => ({ ...prev, orderStatus: currentStatus }));
  }, [currentStatus]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      orderType: "all",
      store: "all",
      customer: "all",
      dateType: "order",
      orderStatus: filters.orderStatus || "all",
      paymentStatus: "all",
    });
    setPage(1);
  };

  const handleExportData = () => {
    // Export functionality
    const csvContent = [
      [
        "SL",
        "Order ID",
        "Order Date",
        "Customer Name",
        "Mobile",
        "Store",
        "Total Amount",
        "Payment Status",
        "Order Status",
      ],
      ...orders.map((order, index) => [
        index + 1,
        order.orderNumber || "",
        new Date(order.createdAt).toLocaleString(),
        order.customer?.fullName || order.customer?.name || `${order.customer?.firstName || ""} ${order.customer?.lastName || ""}`,
        order.customer?.phone || "",
        order.store || "In-house",
        order.totalAmount ? `₹${order.totalAmount.toFixed(2)}` : "₹0.00",
        order.paymentStatus || "unpaid",
        order.status || "pending",
      ]),
    ]
      .map((row) => row.map((val) => `"${val}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "orders_export.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewOrder = (orderId) => {
    navigate(`/dashboard/orders/${orderId}`);
  };

  const handleDownloadInvoice = (orderId) => {
    // Download invoice functionality
    console.log("Downloading invoice for order:", orderId);
  };

  const handleCancelOrder = async (orderId) => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this order? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      console.log("🚫 Admin cancelling order:", orderId);

      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${API_BASE_URL}/orders/${orderId}`,
        {
          status: "cancelled",
          reason: "Cancelled by admin",
        },
        {
        headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("✅ Order cancelled successfully:", response.data);

      // Refresh orders list
      await fetchOrders();
      alert("Order cancelled successfully");
    } catch (error) {
      console.error("❌ Error cancelling order:", error);
      alert(
        `Failed to cancel order: ${error.response?.data?.message || error.message}`,
      );
    }
  };

  // Filter options
  const orderStatusOptions = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "confirmed", label: "Confirmed" },
    { value: "processing", label: "Processing" },
    { value: "out-for-delivery", label: "Out for Delivery" },
    { value: "delivered", label: "Delivered" },
    { value: "returned", label: "Returned" },
    { value: "cancelled", label: "Cancelled" },
    { value: "failed", label: "Failed" },
  ];

  const paymentStatusOptions = [
    { value: "all", label: "All Payment Status" },
    { value: "paid", label: "Paid" },
    { value: "unpaid", label: "Unpaid" },
  ];

  const orderTypeOptions = [
    { value: "all", label: "All Order Types" },
    { value: "delivery", label: "Delivery" },
    { value: "pickup", label: "Pickup" },
  ];

  const storeOptions = [
    { value: "all", label: "All Stores" },
    { value: "in-house", label: "In-house" },
    { value: "vendor", label: "Vendor" },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: "hidden" }}>
      {/* Page Title with Count Badge */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={{ xs: "column", sm: "row" }}
        gap={{ xs: 2, sm: 0 }}
        sx={{ pt: { xs: 2, sm: 1 } }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant={isSmallMobile ? "h5" : "h4"} fontWeight="600">
            {pageTitle}
          </Typography>
          <Badge
            badgeContent={totalOrders}
            color="primary"
            sx={{
              "& .MuiBadge-badge": {
                fontSize: isSmallMobile ? "0.75rem" : "0.875rem",
                fontWeight: 500,
              },
            }}
          >
            <Box />
          </Badge>
        </Box>
      </Box>

      {/* Filter Section */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, bgcolor: "white" }}>
        <Typography variant="h6" mb={2} fontWeight="500">
          Filter Orders
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Order Type</InputLabel>
              <Select
                value={filters.orderType}
                label="Order Type"
                onChange={(e) =>
                  handleFilterChange("orderType", e.target.value)
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="delivery">Delivery</MenuItem>
                <MenuItem value="pickup">Pickup</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Store</InputLabel>
              <Select
                value={filters.store}
                label="Store"
                onChange={(e) => handleFilterChange("store", e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="inhouse">Inhouse</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="both">Both</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Customer</InputLabel>
              <Select
                value={filters.customer}
                label="Customer"
                onChange={(e) => handleFilterChange("customer", e.target.value)}
              >
                <MenuItem value="all">All Customers</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Type</InputLabel>
              <Select
                value={filters.dateType}
                label="Date Type"
                onChange={(e) => handleFilterChange("dateType", e.target.value)}
              >
                <MenuItem value="order">Order Date</MenuItem>
                <MenuItem value="delivery">Delivery Date</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box
              display="flex"
              gap={1}
              justifyContent={{ xs: "stretch", sm: "flex-start" }}
            >
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                sx={{
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": { borderColor: "#9ca3af" },
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={fetchOrders}
                sx={{
                  bgcolor: "#3b82f6",
                  "&:hover": { bgcolor: "#2563eb" },
                  flex: { xs: 1, sm: "none" },
                }}
              >
                Show Data
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Search and Export Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexDirection={{ xs: "column", sm: "row" }}
        >
          <TextField
            fullWidth
            placeholder="Search by Order ID"
            value={search}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: { xs: "100%", sm: 400 } }}
          />
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={handleExportData}
            sx={{
              bgcolor: "#10b981",
              "&:hover": { bgcolor: "#059669" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Export
          </Button>
        </Box>
      </Paper>

      {/* Orders Table - Desktop View */}
      {!isMobile ? (
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
          }}
        >
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 600,
              minWidth: 1200,
              overflowX: "auto",
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: 1200,
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 80,
                    }}
                  >
                    SL
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 180,
                    }}
                  >
                    Order ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 140,
                    }}
                  >
                    Order Date
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 160,
                    }}
                  >
                    Customer Info
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 100,
                    }}
                  >
                    Store
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 140,
                    }}
                  >
                    Total Amount
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 120,
                    }}
                  >
                    Order Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      bgcolor: "#f8fafc",
                      whiteSpace: "nowrap",
                      width: 120,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                          No orders found
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Try adjusting your filters or check back later
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  orders.map((order, index) => (
                    <TableRow
                      key={order._id || index}
                      hover
                      sx={{ "&:hover": { bgcolor: "#f8fafc" } }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {(page - 1) * 10 + index + 1}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
                        {order.orderNumber || `#${order._id?.slice(-8)}`}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="body2">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {order.customer?.fullName || order.customer?.name || `${order.customer?.firstName || ""} ${order.customer?.lastName || ""}`.trim()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.customer?.phone || "N/A"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          label={
                            order.store === "vendor" ? "Vendor" : "In-house"
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor:
                              order.store === "vendor" ? "#f59e0b" : "#6366f1",
                            color:
                              order.store === "vendor" ? "#f59e0b" : "#6366f1",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={500}>
                            {order.totalAmount
                              ? `₹${order.totalAmount.toFixed(2)}`
                              : "₹0.00"}
                          </Typography>
                          <Chip
                            label={
                              order.paymentStatus === "paid" ? "Paid" : "Unpaid"
                            }
                            color={
                              paymentStatusColors[order.paymentStatus] ||
                              "default"
                            }
                            size="small"
                            sx={{ fontSize: "0.75rem", height: 20 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Chip
                          label={
                            order.status?.replace("-", " ").toUpperCase() ||
                            "PENDING"
                          }
                          color={statusColors[order.status] || "default"}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            transition: "all 0.3s ease",
                            cursor: "pointer",
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" gap={1}>
                          <Tooltip title="View Order Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewOrder(order._id)}
                              sx={{
                                color: "#3b82f6",
                                "&:hover": {
                                  bgcolor: "#eff6ff",
                                  transform: "scale(1.1)",
                                  transition: "all 0.2s ease",
                                },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download Invoice PDF">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadInvoice(order._id)}
                              sx={{
                                color: "#10b981",
                                "&:hover": {
                                  bgcolor: "#f0fdf4",
                                  transform: "scale(1.1)",
                                  transition: "all 0.2s ease",
                                },
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {order.status === "pending" && (
                            <Tooltip title="Cancel Order">
                              <IconButton
                                size="small"
                                onClick={() => handleCancelOrder(order._id)}
                                sx={{
                                  color: "#ef4444",
                                  "&:hover": {
                                    bgcolor: "#fef2f2",
                                    transform: "scale(1.1)",
                                    transition: "all 0.2s ease",
                                  },
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        /* Mobile Card View */
        <Box sx={{ width: "100%" }}>
          {orders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No orders found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Try adjusting your filters or check back later
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {orders.map((order, index) => (
                <Card key={order._id || index} sx={{ p: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    {/* Order Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        #{(page - 1) * 10 + index + 1}
                      </Typography>
                      <Chip
                        label={
                          order.status?.replace("-", " ").toUpperCase() ||
                          "PENDING"
                        }
                        color={statusColors[order.status] || "default"}
                        size="small"
                      />
                    </Box>

                    {/* Order Details */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, mb: 1 }}
                      >
                        Order ID:{" "}
                        {order.orderNumber || `#${order._id?.slice(-8)}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Date: {new Date(order.createdAt).toLocaleDateString()}{" "}
                        {new Date(order.createdAt).toLocaleTimeString()}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Customer: {order.customer?.firstName}{" "}
                        {order.customer?.lastName}
                      </Typography>
                      {order.customer?.phone && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          Phone: {order.customer?.phone}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Store:
                        </Typography>
                        <Chip
                          label={
                            order.store === "vendor" ? "Vendor" : "In-house"
                          }
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor:
                              order.store === "vendor" ? "#f59e0b" : "#6366f1",
                            color:
                              order.store === "vendor" ? "#f59e0b" : "#6366f1",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Amount and Payment */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {order.totalAmount
                          ? `₹${order.totalAmount.toFixed(2)}`
                          : "₹0.00"}
                      </Typography>
                      <Chip
                        label={
                          order.paymentStatus === "paid" ? "Paid" : "Unpaid"
                        }
                        color={
                          order.paymentStatus === "paid" ? "success" : "error"
                        }
                        size="small"
                      />
                    </Box>

                    {/* Action Buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1,
                        justifyContent: { xs: "stretch", sm: "flex-end" },
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewOrder(order._id)}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        View
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadInvoice(order._id)}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        Invoice
                      </Button>
                      {order.status === "pending" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => handleCancelOrder(order._id)}
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      )}

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </Box>
      )}
    </Box>
  );
}

export default Orders;
